import type { Session } from "@supabase/supabase-js";

/**
 * Cross-subdomain session bridge.
 *
 * Supabase stores its session in localStorage, which is origin-scoped.
 * That means panel.retaillemon.com and retaillemon.com don't share login state.
 *
 * We mirror the tokens into a cookie scoped to `.retaillemon.com` so the
 * marketing site can hydrate the session when the user is already logged in
 * on the panel.
 */

const COOKIE_NAME = "rl_sess";
const PARENT_DOMAIN = ".retaillemon.com";

interface MiniSession {
  access_token: string;
  refresh_token: string;
  expires_at?: number | null;
}

function shouldUseCookie(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "retaillemon.com" || host.endsWith(".retaillemon.com");
}

function b64encode(str: string): string {
  return typeof btoa !== "undefined" ? btoa(unescape(encodeURIComponent(str))) : str;
}

function b64decode(str: string): string {
  try {
    return typeof atob !== "undefined" ? decodeURIComponent(escape(atob(str))) : str;
  } catch {
    return "";
  }
}

export function writeSessionCookie(session: Session | null): void {
  if (!shouldUseCookie() || !session?.access_token || !session?.refresh_token) return;
  const payload: MiniSession = {
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    expires_at: session.expires_at ?? null,
  };
  const value = b64encode(JSON.stringify(payload));
  // 30 days — refresh tokens are long-lived; we re-write on every TOKEN_REFRESHED.
  const maxAge = 60 * 60 * 24 * 30;
  document.cookie = `${COOKIE_NAME}=${value}; Domain=${PARENT_DOMAIN}; Path=/; Max-Age=${maxAge}; Secure; SameSite=Lax`;
}

export function readSessionCookie(): MiniSession | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;
  const raw = match.slice(COOKIE_NAME.length + 1);
  const decoded = b64decode(raw);
  if (!decoded) return null;
  try {
    const parsed = JSON.parse(decoded) as MiniSession;
    if (!parsed.access_token || !parsed.refresh_token) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearSessionCookie(): void {
  if (!shouldUseCookie()) return;
  document.cookie = `${COOKIE_NAME}=; Domain=${PARENT_DOMAIN}; Path=/; Max-Age=0; Secure; SameSite=Lax`;
}
